// src/pages/purchaseOrder/components/BasicInformationSection.jsx
import React, { useState, useRef } from 'react';
import {
  User,
  FileText,
  Plus,
  X,
  Search,
  AlertCircle
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { searchSuppliers } from '../../../services/api/supplier';
import { SupplierModal } from '../../../components/common';
import Dropdown from '../../../components/ui/Dropdown/Dropdown';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import FieldError from '../../message-center/components/FieldError';
import styles from './BasicInformationSection.module.css';

const BasicInformationSection = ({
  formData,
  warehouses,
  suppliers,
  errors,
  onFieldChange,
  onAddSupplier,
  disabled = false,
  isEditMode = false
}) => {
  const { t } = useLocalization();
  
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [supplierSearchResults, setSupplierSearchResults] = useState([]);
  const [showSupplierResults, setShowSupplierResults] = useState(false);
  const [isSearchingSuppliers, setIsSearchingSuppliers] = useState(false);
  const [selectedSupplierIndex, setSelectedSupplierIndex] = useState(-1);
  const supplierSearchRef = useRef(null);
  
  const [showCreateSupplierModal, setShowCreateSupplierModal] = useState(false);
  const [createSupplierName, setCreateSupplierName] = useState('');

  const selectedSupplier = suppliers.find(s => s.id === formData.supplierId);
  const supplierDisplayText = selectedSupplier ? selectedSupplier.name : '';

  const handleSupplierSearch = async (value) => {
    setSupplierSearchTerm(value);
    
    if (!value.trim()) {
      setSupplierSearchResults([]);
      setShowSupplierResults(false);
      return;
    }

    try {
      setIsSearchingSuppliers(true);
      const response = await searchSuppliers(value, { limit: 10 });
      setSupplierSearchResults(response.suppliers || []);
      setShowSupplierResults(true);
      setSelectedSupplierIndex(-1);
    } catch (error) {
      console.error('Supplier search error:', error);
      setSupplierSearchResults([]);
    } finally {
      setIsSearchingSuppliers(false);
    }
  };

  const handleSupplierSelect = (supplier) => {
    onFieldChange('supplierId', supplier.id);
    setSupplierSearchTerm('');
    setShowSupplierResults(false);
    setSelectedSupplierIndex(-1);
  };

  const handleSupplierKeyDown = (e) => {
    if (!showSupplierResults || supplierSearchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSupplierIndex(prev => 
          prev < supplierSearchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSupplierIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSupplierIndex >= 0 && selectedSupplierIndex < supplierSearchResults.length) {
          handleSupplierSelect(supplierSearchResults[selectedSupplierIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSupplierResults(false);
        setSelectedSupplierIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.basicInfoContent}>
      <div className={styles.formGrid}>
        <div className={styles.formGroup}>
          <label htmlFor="warehouse">
            {t('purchaseOrder.warehouse')} *
          </label>
          <Dropdown
            value={formData.warehouseId || ''}
            onChange={(value) => onFieldChange('warehouseId', value ? parseInt(value, 10) : null)}
            options={warehouses.map(warehouse => ({ value: warehouse.id, label: warehouse.name }))}
            placeholder={t('purchaseOrder.selectWarehouse')}
            searchable
            disabled={disabled}
            className={errors.warehouseId ? styles.controlError : ''}
          />
          <FieldError message={errors.warehouseId} />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="supplier">
            {t('purchaseOrder.supplier')} *
          </label>
          <div className={styles.supplierSearchWrapper}>
            {!formData.supplierId ? (
              <>
                <div className={styles.searchInputWrapper}>
                  <Search className={styles.inputIcon} size={18} />
                  <input
                    ref={supplierSearchRef}
                    type="text"
                    value={supplierSearchTerm}
                    onChange={(e) => handleSupplierSearch(e.target.value)}
                    onKeyDown={handleSupplierKeyDown}
                    onFocus={() => supplierSearchTerm && setShowSupplierResults(true)}
                    onBlur={() => setTimeout(() => setShowSupplierResults(false), 200)}
                    placeholder={t('purchaseOrder.selectSupplier')}
                    className={errors.supplierId ? styles.error : ''}
                    disabled={disabled}
                  />
                  {supplierSearchTerm && (
                    <button
                      type="button"
                      onClick={() => {
                        setSupplierSearchTerm('');
                        setShowSupplierResults(false);
                      }}
                      className={styles.clearButton}
                      disabled={disabled}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {showSupplierResults && !disabled && (
                  <div className={styles.searchResults}>
                    {isSearchingSuppliers ? (
                      <div className={styles.searchLoading}>
                        <div className={styles.loadingSpinner}></div>
                        <span>{t('purchaseOrder.searchingSuppliers')}</span>
                      </div>
                    ) : supplierSearchResults.length > 0 ? (
                      <>
                        <div className={styles.resultsList}>
                          {supplierSearchResults.map((supplier, index) => (
                            <div
                              key={supplier.id}
                              className={`${styles.resultItem} ${
                                index === selectedSupplierIndex ? styles.selected : ''
                              }`}
                              onClick={() => handleSupplierSelect(supplier)}
                            >
                              <div className={styles.supplierInfo}>
                                <div className={styles.supplierName}>{supplier.name}</div>
                                {supplier.contactPerson && (
                                  <div className={styles.supplierDetail}>
                                    {supplier.contactPerson}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className={styles.createSupplierButton}
                          onClick={() => {
                            setCreateSupplierName('');
                            setShowCreateSupplierModal(true);
                            setShowSupplierResults(false);
                          }}
                        >
                          <Plus size={16} />
                          {t('purchaseOrder.createNewSupplier')}
                        </button>
                      </>
                    ) : (
                      <div className={styles.noResults}>
                        <AlertCircle size={16} />
                        <span>{t('purchaseOrder.noSuppliersAvailable')}</span>
                        <button
                          type="button"
                          className={styles.createSupplierButtonInline}
                          onClick={() => {
                            setCreateSupplierName(supplierSearchTerm.trim());
                            setShowCreateSupplierModal(true);
                            setShowSupplierResults(false);
                          }}
                        >
                          <Plus size={14} />
                          {t('purchaseOrder.createSupplierWithName', { name: supplierSearchTerm })}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className={styles.selectedSupplier}>
                <User className={styles.inputIcon} size={18} />
                <span className={styles.selectedSupplierName}>{supplierDisplayText}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => onFieldChange('supplierId', null)}
                    className={styles.clearSupplierButton}
                    title={t('purchaseOrder.changeSupplier')}
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
          <FieldError message={errors.supplierId} />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="orderDate">
            {t('purchaseOrder.orderDate')} *
          </label>
          <DatePicker
            value={formData.orderDate}
            onChange={(value) => onFieldChange('orderDate', value)}
            placeholder={t('purchaseOrder.orderDate')}
            disabled={disabled}
            className={errors.orderDate ? styles.controlError : ''}
          />
          <FieldError message={errors.orderDate} />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="expectedDeliveryDate">
            {t('purchaseOrder.expectedDeliveryDate')}
          </label>
          <DatePicker
            value={formData.expectedDeliveryDate}
            onChange={(value) => onFieldChange('expectedDeliveryDate', value)}
            placeholder={t('purchaseOrder.expectedDeliveryDate')}
            minDate={formData.orderDate}
            disabled={disabled}
          />
        </div>

        {(!isEditMode || formData.status === 'draft') && (
        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={formData.autoReceive}
              onChange={(e) => onFieldChange('autoReceive', e.target.checked)}
              className={styles.checkbox}
              disabled={disabled}
            />
            <span>{t('purchaseOrder.autoReceive')}</span>
            <span className={styles.tooltip} title={t('purchaseOrder.autoReceiveTooltip')}>
              <AlertCircle size={14} />
            </span>
          </label>
        </div>
        )}

        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
          <label htmlFor="notes">
            {t('purchaseOrder.notes')}
          </label>
          <div className={styles.inputWrapper}>
            <FileText className={`${styles.inputIcon} ${styles.textareaIcon}`} size={18} />
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => onFieldChange('notes', e.target.value)}
              placeholder={t('purchaseOrder.notesPlaceholder')}
              rows={3}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {showCreateSupplierModal && (
        <SupplierModal
          mode="create"
          initialName={createSupplierName}
          onClose={() => setShowCreateSupplierModal(false)}
          onSuccess={(supplier) => {
            setShowCreateSupplierModal(false);
            onAddSupplier(supplier);
          }}
        />
      )}
    </div>
  );
};

export default BasicInformationSection;
